import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiAnchor, FiClock, FiMapPin, FiShield, FiArrowRight, FiCheck } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { destinations, SITE_URL, PORT_COORDS } from '../config/seo-data';

const buildBookingUrl = (pickup, dropoff) => {
  const params = new URLSearchParams();
  if (pickup) {
    params.set('pickup_name', pickup.name);
    params.set('pickup_lat', pickup.lat);
    params.set('pickup_lng', pickup.lng);
  }
  if (dropoff) {
    params.set('dropoff_name', dropoff.name);
    params.set('dropoff_lat', dropoff.lat);
    params.set('dropoff_lng', dropoff.lng);
  }
  return `/booking?${params.toString()}`;
};

const ease = [0.22, 1, 0.36, 1];

const portRoutes = [
  { name: 'Corfu Old Town', time: '5 min', slug: 'corfu-town', desc: 'UNESCO World Heritage site', coords: { lat: 39.6243, lng: 19.9213 } },
  { name: 'Paleokastritsa', time: '35 min', slug: 'paleokastritsa', desc: 'Crystal bays & monastery', coords: { lat: 39.6787, lng: 19.7034 } },
  { name: 'Kassiopi', time: '55 min', slug: 'kassiopi', desc: 'Charming fishing village', coords: { lat: 39.7896, lng: 19.9139 } },
  { name: 'Sidari', time: '50 min', slug: 'sidari', desc: 'Canal d\'Amour & beaches', coords: { lat: 39.7915, lng: 19.6798 } },
  { name: 'Gouvia', time: '15 min', slug: 'gouvia', desc: 'Marina & Venetian shipyard', coords: { lat: 39.6530, lng: 19.8490 } },
  { name: 'Dassia', time: '20 min', slug: 'dassia', desc: 'Family-friendly resort', coords: { lat: 39.6750, lng: 19.8540 } },
];

const cruiseFeatures = [
  'Pickup at cruise terminal exit',
  'Shore excursion packages (4-8 hours)',
  'Guaranteed return before ship departure',
  'Flexible timing around your schedule',
  'Professional English-speaking drivers',
  'Mercedes-Benz with air conditioning',
];

const PortTransfers = () => (
  <SEOPageLayout
    title="Lasmari Chauffeurs | Corfu Port & Cruise Transfers"
    description="Reliable Corfu port transfers from cruise terminal and ferry port. Private pickup for cruise passengers, shore excursions, and ferry arrivals. Book online."
    keywords="corfu port transfer, corfu cruise port transfer, corfu cruise ship transfer, corfu port taxi, corfu ferry port transfer, corfu shore excursion transfer"
    canonical={`${SITE_URL}/port-transfers`}
  >
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/images/backgrounds/port-vclass-hero.webp')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 45%, rgba(0,0,0,0.55), transparent 72%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="text-white/85 text-xs uppercase tracking-[0.4em] mb-6 font-light"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Cruise Terminal · Ferry Port
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 0.1 }}
          className="text-5xl md:text-6xl lg:text-7xl text-white mb-8 leading-[1.08] tracking-tight"
          style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 18px rgba(0,0,0,0.6)' }}
        >
          Corfu Port
          <br />
          <span className="font-light italic">Transfers</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.2 }}
          className="text-gray-300 text-lg lg:text-xl max-w-2xl mx-auto mb-10 font-light leading-relaxed"
          style={{ textShadow: '0 1px 3px rgba(0,0,0,0.85), 0 2px 16px rgba(0,0,0,0.6)' }}
        >
          Arriving by cruise ship or ferry? We meet you at the terminal with a Mercedes-Benz and take you anywhere on the island.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
        >
          <Link
            to={`/booking?pickup_name=${encodeURIComponent(PORT_COORDS.name)}&pickup_lat=${PORT_COORDS.lat}&pickup_lng=${PORT_COORDS.lng}`}
            className="bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Book Port Transfer
          </Link>
        </motion.div>
      </div>
    </section>

    {/* Two Column - Cruise & Ferry */}
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
          {/* Cruise Service */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">For Cruise Passengers</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1a1012] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Shore Excursion
              <br /><span className="italic">Service</span>
            </h2>
            <p className="text-[#5b4d4f] mb-8 leading-relaxed font-light">
              Corfu welcomes over 500 cruise ships annually. We specialise in time-sensitive transfers, ensuring you maximise your limited time on the island and return safely before departure.
            </p>
            <div className="space-y-4">
              {cruiseFeatures.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-[#fbeceb] border border-[#ba1609]/20 flex items-center justify-center shrink-0">
                    <FiCheck className="w-3 h-3 text-[#ba1609]" />
                  </div>
                  <span className="text-[#5b4d4f] text-sm font-light">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Ferry Service - Route Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease }}
          >
            <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Popular Port Routes</p>
            <h2 className="text-3xl lg:text-4xl font-light text-[#1a1012] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Where We
              <br /><span className="italic">Take You</span>
            </h2>
            <p className="text-[#5b4d4f] mb-8 leading-relaxed font-light">
              From Igoumenitsa, Paxos, or Italy, we transfer from the ferry terminal to your destination.
            </p>
            <div className="space-y-3">
              {portRoutes.map((r, i) => (
                <motion.div
                  key={r.slug}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease }}
                >
                  <div className="group bg-[#faf7f6] border border-black/10 rounded-xl px-5 py-4 hover:bg-white hover:border-[#ba1609]/30 hover:shadow-sm transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <Link to={`/transfers/${r.slug}?from=port`} className="flex-1">
                        <h3 className="text-[#1a1012] font-medium group-hover:text-[#ba1609] transition-colors mb-0.5">{r.name}</h3>
                        <div className="flex items-center gap-2 text-[#5b4d4f] text-xs">
                          <span>{r.time}</span>
                          <span className="w-1 h-1 rounded-full bg-black/20" />
                          <span>{r.desc}</span>
                        </div>
                      </Link>
                      <Link
                        to={buildBookingUrl(PORT_COORDS, { name: r.name, ...r.coords })}
                        className="text-[11px] bg-[#ba1609] hover:bg-[#8e1107] text-white px-3 py-1.5 rounded-full transition-all duration-300 shrink-0 ml-3"
                      >
                        Book
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* All Destinations */}
    <section className="py-24 lg:py-32 bg-[#faf7f6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">Full Coverage</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            All Destinations
          </h2>
          <p className="text-[#5b4d4f] text-sm font-light max-w-lg mx-auto">
            We transfer to every destination in Corfu from the cruise port and ferry terminal
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {destinations.filter((d) => !['South Corfu', 'Southeast Corfu'].includes(d.area)).slice(0, 12).map((d, i) => (
            <motion.div
              key={d.slug}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04, ease }}
            >
              <div className="group bg-white border border-black/10 rounded-xl px-5 py-4 hover:border-[#ba1609]/30 hover:shadow-sm transition-all duration-300">
                <div className="flex items-center justify-between">
                  <Link to={`/transfers/${d.slug}?from=port`} className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[#1a1012] font-medium group-hover:text-[#ba1609] transition-colors">{d.name}</h3>
                      {d.popular && (
                        <span className="text-[9px] bg-[#fbeceb] text-[#ba1609] px-2 py-0.5 rounded-full uppercase tracking-wider border border-[#ba1609]/20">
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[#5b4d4f] text-xs">
                      <span>{d.distance}</span>
                      <span className="w-1 h-1 rounded-full bg-black/20" />
                      <span>{d.duration}</span>
                    </div>
                  </Link>
                  {d.coords && (
                    <Link
                      to={buildBookingUrl(PORT_COORDS, { name: d.name, ...d.coords })}
                      className="text-[11px] bg-[#ba1609] hover:bg-[#8e1107] text-white px-3 py-1.5 rounded-full transition-all duration-300 shrink-0 ml-3"
                    >
                      Book
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </SEOPageLayout>
);

export default PortTransfers;
